/**
 * Production-safe logger utility
 * - dev(): Only logs in development mode
 * - prod(): Logs errors/warnings in production, all levels in dev
 *
 * Log persistence:
 * - initLogStore() installs console interceptors that save the last 500
 *   entries to localStorage so logs survive page refreshes.
 * - downloadLogs() exports everything as a timestamped .txt file.
 * - clearLogs() wipes the buffer and storage.
 */

type LogLevel = 'info' | 'warn' | 'error';

export const logger = {
  dev: (...args: any[]) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(...args);
    }
  },

  prod: (level: LogLevel, ...args: any[]) => {
    if (level === 'error' || level === 'warn') {
      console[level](...args);
    } else if (process.env.NODE_ENV === 'development') {
      console.info(...args);
    }
  },

  error: (...args: any[]) => { console.error(...args); },
  warn:  (...args: any[]) => { console.warn(...args); },
};

// ─── Persistent log store ────────────────────────────────────────────────────

const STORAGE_KEY = 'wasl_pos_logs';
const MAX_ENTRIES = 500;

export interface LogEntry {
  ts: string;
  level: 'LOG' | 'WARN' | 'ERROR' | 'INFO';
  msg: string;
}

let _buffer: LogEntry[] = [];
let _installed = false;

function _loadFromStorage(): LogEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LogEntry[]) : [];
  } catch {
    return [];
  }
}

function _persist() {
  try {
    if (_buffer.length > MAX_ENTRIES) _buffer = _buffer.slice(_buffer.length - MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(_buffer));
  } catch {
    _buffer = _buffer.slice(Math.floor(_buffer.length / 2));
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_buffer)); } catch { /* ignore */ }
  }
}

function _fmt(args: unknown[]): string {
  return args.map(a => {
    if (typeof a === 'string') return a;
    try { return JSON.stringify(a); } catch { return String(a); }
  }).join(' ');
}

function _push(level: LogEntry['level'], args: unknown[]) {
  _buffer.push({ ts: new Date().toISOString(), level, msg: _fmt(args) });
  _persist();
}

/**
 * Install console interceptors. Call once in main.tsx.
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export function initLogStore() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;
  _buffer = _loadFromStorage();

  const _log   = console.log.bind(console);
  const _warn  = console.warn.bind(console);
  const _error = console.error.bind(console);
  const _info  = console.info.bind(console);

  console.log   = (...a: unknown[]) => { _log(...a);   _push('LOG',   a); };
  console.warn  = (...a: unknown[]) => { _warn(...a);  _push('WARN',  a); };
  console.error = (...a: unknown[]) => { _error(...a); _push('ERROR', a); };
  console.info  = (...a: unknown[]) => { _info(...a);  _push('INFO',  a); };

  window.addEventListener('unhandledrejection', (e) => {
    _push('ERROR', [`[unhandledrejection] ${e.reason}`]);
  });

  _push('LOG', [`[LogStore] Ready — ${_buffer.length} prior entries loaded`]);
}

/** Return a snapshot of all stored entries. */
export function getLogs(): LogEntry[] { return [..._buffer]; }

/** Download all stored logs as a plain-text file. */
export function downloadLogs() {
  const text = _buffer.map(e => `${e.ts} [${e.level}] ${e.msg}`).join('\n');
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `wasl-pos-logs-${new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Clear the in-memory buffer and localStorage. */
export function clearLogs() {
  _buffer = [];
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
